import { useCallback, useState } from 'react';

import { useToastContext } from '@rtkwlf/fenrir-react';
import { captureException } from '@sentry/react';
import { useTranslation } from 'react-i18next';

import { useAuthContext } from '@packages/legacy/auth';
import { TOAST_DURATION } from 'ae-constants/toast';

export type DownloadType = {
  isFetching: boolean;
  error: unknown | null;
  download: () => void;
  setError: (error: unknown | null) => void;
};

const FILENAME_HEADER = 'x-client-filename';
export const useDownloadFile = (
  fileName: string,
  url: RequestInfo,
  options?: RequestInit,
  onSuccess?: () => void,
): DownloadType => {
  const { fetchApi } = useAuthContext();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const download = useCallback(() => {
    setError(null);
    setIsFetching(false);

    async function downloadFile() {
      try {
        setIsFetching(true);
        const headers = new Headers(options?.headers);
        // filename has to be ISO-8859-1 characters
        headers.set(FILENAME_HEADER, fileName.replaceAll('–', '-'));

        const res = await fetchApi(url, { ...options, headers });
        if (!res.ok) {
          throw new Error(`Error downloading file: ${fileName}`);
        }
        const text = await res.text();
        const downloadUrl = window.URL.createObjectURL(new Blob([text]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        if (onSuccess) {
          onSuccess();
        }
      } catch (err) {
        setIsFetching(false);
        setError(err);
        captureException(err, { tags: { action: 'file-download' } });
        addToast({
          appearance: 'danger',
          duration: TOAST_DURATION,
          message: t('detailsPage:exportErrorMessage'),
          title: t('detailsPage:exportErrorTitle'),
        });
      } finally {
        setIsFetching(false);
      }
    }

    downloadFile();
  }, [options, fileName, fetchApi, url, onSuccess, addToast, t]);

  return { download, error, isFetching, setError };
};
